import { format, parseISO } from "date-fns";
import { useAgentRunControls } from "@api/agentRun.api";
import { AGENT_RUN_WAITING_ON_PROPOSAL_TEXT } from "@common/constants/domains/agentRuns/agentRunCopy";
import { AgentRunSummary } from "@common/schemas/domains/agentRuns/agent-run.schema";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import {
    PresenceStack,
    PresenceStackMember,
} from "@components/avatar/PresenceStack";
import { AgentRunActivityDrawer } from "@components/presence/AgentRunActivityDrawer";
import { StateAwareLink } from "@components/routes/StateAwareLink";
import type { BoardCardField } from "@design-system/Board/Board.types";
import { BoardCard } from "@design-system/Board/BoardCard";
import { BoardCardFocusContext } from "@design-system/Board/boardCardFocus";
import { BoardCardLayout } from "@design-system/Board/BoardCardLayout";
import { BoardTruncatedText } from "@design-system/Board/BoardTruncatedText";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Link } from "@design-system/Link/Link";
import { Tag } from "@design-system/Tag/Tag";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { TasksIcon } from "@icons";
import {
    type AgentRunStatus,
    getAgentRunStatus,
    isWaitingOnProposalsOnly,
    PAUSABLE_KINDS,
    CONTINUABLE_KINDS,
    STOPPABLE_KINDS,
} from "./agentRunStatus";
import { useAgentRunTaskPath } from "./agentRunPaths";
import styles from "./AgentRunsPanel.module.scss";

export const STOP_TOOLTIP = "Stops after the current step";
export const PROPOSAL_WAIT_TOOLTIP = AGENT_RUN_WAITING_ON_PROPOSAL_TEXT;

interface AgentRunRowProps {
    run: AgentRunSummary;
    /** Organization chat: the row names its client and links to the organization task page. */
    isOrganizationChat?: boolean;
}

/** The agent, live while it runs, then who started the run. */
const getPresenceMembers = ({
    run,
    status,
}: {
    run: AgentRunSummary;
    status: AgentRunStatus;
}): PresenceStackMember[] => [
    {
        kind: "agent",
        detail: status.label,
        isLive: status.kind === "running",
    },
    {
        kind: "person",
        id: `starter-${run.startedBy.id}`,
        name: run.startedBy.name,
        user: run.startedBy,
        role: "pinned",
        detail: "Started this run",
        isLive: false,
    },
];

/**
 * The card's rows below the title, as a board card lays them out: a
 * `Client · Started by · Close` context line, the run's tags (close tasks
 * done, start date, stage), and its people bottom right.
 */
const getRunCardFields = ({
    run,
    status,
    isOrganizationChat,
}: {
    run: AgentRunSummary;
    status: AgentRunStatus;
    isOrganizationChat: boolean;
}): BoardCardField[] => {
    const contextText = [
        isOrganizationChat ? run.workspaceName : null,
        run.startedBy.name,
        run.close?.name ?? null,
    ]
        .filter((part) => part !== null && part !== "")
        .join(" · ");
    return [
        {
            id: "context",
            placement: "stacked",
            content: <BoardTruncatedText>{contextText}</BoardTruncatedText>,
        },
        {
            id: "closeTasks",
            placement: "inline",
            content: run.closeTasks !== null && (
                <Tag size="sm" color="neutral" outline leftIcon={<TasksIcon />}>
                    {run.closeTasks.done}/{run.closeTasks.total}
                </Tag>
            ),
        },
        {
            id: "startedAt",
            placement: "inline",
            content: (
                <Tag size="sm" color="neutral" outline>
                    {format(parseISO(run.createdAt), "MMM d")}
                </Tag>
            ),
        },
        {
            id: "stage",
            placement: "inline",
            content: run.stage.label !== null && (
                <Tag size="sm" color="neutral" outline>
                    {run.stage.label}
                </Tag>
            ),
        },
        {
            id: "presence",
            placement: "bottomEnd",
            content: (
                <PresenceStack members={getPresenceMembers({ run, status })} />
            ),
        },
    ];
};

/** Pause, Continue and Stop, each while the run's state allows it. */
const AgentRunControlButtons = ({ run }: { run: AgentRunSummary }) => {
    const controls = useAgentRunControls();
    const status = getAgentRunStatus(run);
    const isProposalOnly = isWaitingOnProposalsOnly(run);
    return (
        <HStack gap={8} align="center">
            {PAUSABLE_KINDS.has(status.kind) ? (
                <Button
                    size="xs"
                    variant="secondary"
                    disabled={controls.isBusy}
                    onClick={() => controls.onPause(run.id)}
                >
                    Pause
                </Button>
            ) : null}
            {CONTINUABLE_KINDS.has(status.kind) ? (
                <Tooltip
                    content={isProposalOnly ? PROPOSAL_WAIT_TOOLTIP : undefined}
                >
                    <Button
                        size="xs"
                        variant="secondary"
                        disabled={controls.isBusy || isProposalOnly}
                        onClick={() => controls.onContinue(run.id)}
                    >
                        Continue
                    </Button>
                </Tooltip>
            ) : null}
            {STOPPABLE_KINDS.has(status.kind) ? (
                <Tooltip content={STOP_TOOLTIP}>
                    <Button
                        size="xs"
                        variant="secondary"
                        disabled={controls.isBusy}
                        onClick={() => controls.onStop(run.id)}
                    >
                        Stop
                    </Button>
                </Tooltip>
            ) : null}
        </HStack>
    );
};

/**
 * One run, as a board card (`BoardCard`, `BoardCardLayout`) with its
 * controls under the fields and its activity drawer. Only the title links to
 * the run's task; the card itself doesn't open. The avatars come with the
 * summary; only the controls and the activity read the scoped workspace, so
 * the organization panel wraps each row in the run's workspace sub-scope
 * without a fetch per client.
 */
export const AgentRunRow = ({
    run,
    isOrganizationChat = false,
}: AgentRunRowProps) => {
    const taskPathFor = useAgentRunTaskPath();
    const status = getAgentRunStatus(run);

    return (
        <article>
            <BoardCard
                drawer={
                    <BoardCardFocusContext.Provider value>
                        <AgentRunActivityDrawer run={run} />
                    </BoardCardFocusContext.Provider>
                }
            >
                <BoardCardLayout
                    leading={
                        run.entity === null ? undefined : (
                            <EntityAvatar
                                entity={run.entity}
                                size="xs"
                                aria-hidden
                            />
                        )
                    }
                    title={
                        <Link
                            className={styles.titleLink}
                            render={
                                <StateAwareLink
                                    to={taskPathFor(
                                        run.runTaskId,
                                        run.workspaceId,
                                    )}
                                />
                            }
                        >
                            {run.entity?.name ?? "Entity"}
                        </Link>
                    }
                    fields={getRunCardFields({
                        run,
                        status,
                        isOrganizationChat,
                    })}
                />
                <AgentRunControlButtons run={run} />
            </BoardCard>
        </article>
    );
};
